import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getActiveSchoolId } from '../utils/activeSchool';
import FormField, { PosInput, PosSelect } from '../components/pos/FormField';
import {
    listNomenclatureGroups, listNomenclature, createNomenclature, updateNomenclature,
    archiveNomenclature, downloadImportTemplate
} from '../services/nomenclatureApi';
import { getReferences } from '../services/integrationApi';

export default function NomenclaturePage() {
    const { t } = useTranslation(['nomenclature', 'common']);
    const schoolId = getActiveSchoolId();
    const emptyForm = () => ({
        name: '', article: '', price: 0, nomenclatureType: 'product', unitName: t('nomenclature:defaultUnit'),
        vatRate: 'none', showOnPosPanel: false, emoji: '', trackStock: false, stockQuantity: 0
    });
    const [groups, setGroups] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [editingId, setEditingId] = useState(null);
    const [vatOptions, setVatOptions] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [units, setUnits] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!schoolId) return;
        load();
    }, [schoolId, selectedGroup]);

    async function load() {
        try {
            const [g, i, vat, unitRefs, types] = await Promise.all([
                listNomenclatureGroups(schoolId),
                listNomenclature(schoolId, selectedGroup || undefined),
                getReferences('vat_rate', schoolId),
                getReferences('unit', schoolId),
                getReferences('nomenclature_type', schoolId)
            ]);
            setGroups(g.data || []);
            setItems(i.data || []);
            setVatOptions((vat || []).map(x => ({ code: x.code, label: x.name })));
            setTypeOptions((types || []).map(x => ({ code: x.code, label: x.name })));
            setUnits((unitRefs || []).map(x => ({ id: x.id, shortName: x.shortName || x.code, name: x.name })));
        } catch {
            setError(t('nomenclature:loadError'));
        }
    }

    const flatGroups = useMemo(() => {
        const out = [{ id: null, name: t('nomenclature:allProducts') }];
        function walk(nodes, prefix = '') {
            nodes.forEach(n => {
                out.push({ id: n.id, name: prefix + n.name });
                if (n.children?.length) walk(n.children, prefix + '  ');
            });
        }
        walk(groups);
        return out;
    }, [groups, t]);

    async function save() {
        if (!form.name.trim()) return;
        try {
            if (editingId) await updateNomenclature(editingId, schoolId, form);
            else await createNomenclature(schoolId, form);
            setShowForm(false);
            setForm(emptyForm());
            setEditingId(null);
            load();
        } catch (e) {
            setError(e.response?.data?.message || t('nomenclature:saveError'));
        }
    }

    return (
        <OwnerPageLayout title={t('nomenclature:title')} className="pos-page pos-ui" maxWidth={1200}>
            <div className="pos-page__layout">
                <aside className="pos-page__sidebar">
                    <h2 style={{ fontSize: 15, margin: '0 0 12px', fontWeight: 700 }}>{t('nomenclature:groups')}</h2>
                    {flatGroups.map(g => (
                        <button key={g.id ?? 'all'} type="button"
                            className={`pos-tree-btn${selectedGroup === g.id ? ' pos-tree-btn--active' : ''}`}
                            onClick={() => setSelectedGroup(g.id)}>
                            {g.name}
                        </button>
                    ))}
                </aside>
                <main className="pos-page__main">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                        <div />
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" className="pos-btn pos-btn--ghost" onClick={() => downloadImportTemplate().then(r => {
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement('a');
                                a.href = url; a.download = 'nomenclature-import-template.xlsx'; a.click();
                            })}>{t('nomenclature:excelTemplate')}</button>
                            <button type="button" className="pos-btn pos-btn--primary"
                                onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm()); }}>
                                {t('nomenclature:add')}
                            </button>
                            <Link to="/cashier" className="pos-btn pos-btn--ghost" style={{ textDecoration: 'none' }}>{t('nomenclature:backCashier')}</Link>
                        </div>
                    </div>
                    {error && <p className="cashier-error">{error}</p>}
                    <table className="pos-table">
                        <thead>
                            <tr>
                                <th>{t('nomenclature:colName')}</th><th>{t('nomenclature:colArticle')}</th><th>{t('nomenclature:colPrice')}</th><th>{t('nomenclature:colVat')}</th><th>{t('nomenclature:colStock')}</th><th>{t('nomenclature:colPos')}</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(it => (
                                <tr key={it.id}>
                                    <td>{it.name}</td>
                                    <td>{it.article}</td>
                                    <td>{it.price} ₽</td>
                                    <td>{it.vatRate}</td>
                                    <td>{it.trackStock ? it.stockQuantity : '—'}</td>
                                    <td>{it.showOnPosPanel ? '✓' : ''}</td>
                                    <td>
                                        <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '6px 10px', marginRight: 4 }}
                                            onClick={() => { setEditingId(it.id); setForm({ ...it }); setShowForm(true); }}>{t('nomenclature:editShort')}</button>
                                        <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '6px 10px' }}
                                            onClick={() => archiveNomenclature(it.id, schoolId).then(load)}>{t('nomenclature:archive')}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {showForm && (
                        <div className="pos-modal-backdrop" onClick={() => setShowForm(false)}>
                            <div className="pos-modal" onClick={e => e.stopPropagation()}>
                                <h3>{editingId ? t('nomenclature:editTitle') : t('nomenclature:newTitle')}</h3>
                                <div className="pos-form-grid">
                                    <FormField label={t('nomenclature:fieldName')}>
                                        <PosInput value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                    </FormField>
                                    <FormField label={t('nomenclature:fieldArticle')}>
                                        <PosInput value={form.article || ''} onChange={e => setForm({ ...form, article: e.target.value })} />
                                    </FormField>
                                    <FormField label={t('nomenclature:fieldPrice')}>
                                        <PosInput type="number" value={form.price}
                                            onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
                                    </FormField>
                                    <FormField label={t('nomenclature:fieldType')}>
                                        <PosSelect value={form.nomenclatureType} onChange={e => setForm({ ...form, nomenclatureType: e.target.value })}>
                                            {typeOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                                        </PosSelect>
                                    </FormField>
                                    <FormField label={t('nomenclature:fieldUnit')}>
                                        <PosSelect value={form.unitName} onChange={e => setForm({ ...form, unitName: e.target.value })}>
                                            {units.map(u => <option key={u.id} value={u.shortName}>{u.name}</option>)}
                                        </PosSelect>
                                    </FormField>
                                    <FormField label={t('nomenclature:fieldVat')}>
                                        <PosSelect value={form.vatRate} onChange={e => setForm({ ...form, vatRate: e.target.value })}>
                                            {vatOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                                        </PosSelect>
                                    </FormField>
                                    <label className="pos-check">
                                        <input type="checkbox" checked={form.showOnPosPanel}
                                            onChange={e => setForm({ ...form, showOnPosPanel: e.target.checked })} />
                                        {t('nomenclature:showOnPos')}
                                    </label>
                                    <FormField label={t('nomenclature:fieldEmoji')} hint={t('nomenclature:emojiHint')}>
                                        <PosInput value={form.emoji || ''} onChange={e => setForm({ ...form, emoji: e.target.value })} maxLength={4} />
                                    </FormField>
                                </div>
                                <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                                    <button type="button" className="pos-btn pos-btn--primary" onClick={save}>{t('common:save')}</button>
                                    <button type="button" className="pos-btn pos-btn--ghost" onClick={() => setShowForm(false)}>{t('common:cancel')}</button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </OwnerPageLayout>
    );
}
